import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { getFlightAuthoritativeTimes } from "../../../../utils/timeUtils";

dayjs.extend(utc);

export const OPERATIONAL_OVERLAP_BUFFER_MINUTES = 15;

function isForwardRange(start, end) {
    if (!start || !end) return false;
    const startUTC = dayjs.utc(start);
    const endUTC = dayjs.utc(end);
    return startUTC.isValid() && endUTC.isValid() && endUTC.isAfter(startUTC);
}

/**
 * 时间轴画条带必须「到达晚于起飞」。
 * 起飞/到达各自降级时，只有 ETA、没有 ETD 会把 STD 对上一个更早的 ETA，
 * vis-timeline 会把这段画成一条线。预计时刻一旦倒过来，回退到计划时刻。
 */
export function resolveForwardTimelineTimes(flight, timelineType = "actual") {
    const times = getFlightAuthoritativeTimes(flight, timelineType);
    if (isForwardRange(times.departureTime, times.arrivalTime)) return times;

    if (times.arrivalField === "eta" && isForwardRange(times.departureTime, flight.sta)) {
        return { ...times, arrivalTime: flight.sta, arrivalStrategy: "scheduled", arrivalField: "sta" };
    }

    if (times.departureField === "etd" && isForwardRange(flight.std, times.arrivalTime)) {
        return { ...times, departureTime: flight.std, departureStrategy: "scheduled", departureField: "std" };
    }

    if (times.departureStrategy === "scheduled" && isForwardRange(flight.std, flight.sta)) {
        return {
            departureTime: flight.std,
            arrivalTime: flight.sta,
            departureStrategy: "scheduled",
            arrivalStrategy: "scheduled",
            departureField: "std",
            arrivalField: "sta",
        };
    }

    return { ...times, arrivalTime: null, arrivalStrategy: "none", arrivalField: null };
}

function resolveInterval(flight, timelineType) {
    const times = resolveForwardTimelineTimes(flight, timelineType);
    const start = times.departureTime;
    let end = times.arrivalTime;

    if (!end && start) end = dayjs.utc(start).add(2, "hour").toISOString();
    if (!start || !end) return null;

    const startUTC = dayjs.utc(start);
    const endUTC = dayjs.utc(end);
    if (!startUTC.isValid() || !endUTC.isValid() || !endUTC.isAfter(startUTC)) return null;

    return { id: flight.id, start: startUTC, end: endUTC };
}

export function detectSameAircraftOverlaps(flightsToCheck, timelineType = "actual", bufferMinutes = OPERATIONAL_OVERLAP_BUFFER_MINUTES) {
    const intervals = flightsToCheck.map((flight) => resolveInterval(flight, timelineType)).filter(Boolean);
    const overlappingFlightIds = new Set();

    for (let i = 0; i < intervals.length; i++) {
        for (let j = i + 1; j < intervals.length; j++) {
            const first = intervals[i];
            const second = intervals[j];
            const firstIsEarlier = first.start.isBefore(second.start) || (first.start.isSame(second.start) && String(first.id) <= String(second.id));
            const earlier = firstIsEarlier ? first : second;
            const later = firstIsEarlier ? second : first;

            if (earlier.end.add(bufferMinutes, "minute").isAfter(later.start)) {
                overlappingFlightIds.add(first.id);
                overlappingFlightIds.add(second.id);
            }
        }
    }

    return overlappingFlightIds;
}

export function resolveTimelineOverlapIds(flightsToCheck) {
    return {
        planOverlapIds: detectSameAircraftOverlaps(flightsToCheck, "plan"),
        actualOverlapIds: detectSameAircraftOverlaps(flightsToCheck, "actual"),
    };
}
